import { Component } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { ProductionTool } from "@interfaces/models/productionTool.interface";
import { ToolOrder } from "@interfaces/models/toolOrder.interface";
import { Operation } from "@interfaces/models/operation.interface";
import { DEFAULT_BILLET_PLAN_INPUTS, computeBilletPlan, densityKgM3ForMaterialType, rectangularMassKg } from "@helpers/billetPlanning";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { BlockStatusEnum } from "@enums/blockStatus.enum";
import { MaterialType } from "@enums/materialType.enum";
import { PurchaseTypeEnum } from "@enums/purchaseType.enum";
import { environment } from "@environments/environment";
import { FileService } from "@services/file.service";
import { Machine } from "@interfaces/models/machine.interface";
import { Process } from "@interfaces/process.interface";
import { PurchasableItem } from "@interfaces/purchasableItem.interface";
import { UserProfile } from "@interfaces/userProfile.interface";
import { MachineService } from "@services/machine.service";
import { ProcessService } from "@services/process.service";
import { PurchasableItemService } from "@services/purchasableItem.service";
import { ProductionToolPartService } from "@services/productionToolPart.service";
import { ProductionToolService } from "@services/productionTool.service";
import { UserProfileService } from "@services/userProfile.service";
import { ToolOrderService } from "@services/toolOrder.service";
import { OperationService } from "@services/operation.service";
import { ToolroomProcess } from "@interfaces/models/toolroomProcess.interface";
import { ToolroomProcessService } from "@services/toolroomProcess.service";
import { ToastService } from "angular-toastify";
import { forkJoin, from, of } from "rxjs";
import { concatMap, switchMap, toArray } from "rxjs/operators";
import { ToastApiError } from "@helpers/apiError";

@Component({
	moduleId: module.id,
	selector: "toolOrders-id",
	styleUrls: ["toolOrders-id.css"],
	templateUrl: "toolOrders-id.html"
})
export class ToolOrdersIdAdminComponent {
	// Values shown on the header card above the tabs.
	get billetSizeSummary(): string {
		const size = [this.item?.billetLength, this.item?.billetWidth, this.item?.billetHeight];
		return size.some((n) => n) ? size.map((n) => n || "?").join(" × ") + " mm" : "—";
	}

	// Raw billet weight from its size and material, using the same densities as the estimator.
	// Worked out rather than stored, so it always follows the size. null until there's enough to go on.
	get billetWeightKg(): number | null {
		const { billetHeight, billetLength, billetWidth, material } = this.item || ({} as any);

		if (!billetLength || !billetWidth || !billetHeight || !material) {
			return null;
		}

		const density = densityKgM3ForMaterialType(material);
		return density > 0 ? rectangularMassKg(density, billetLength, billetWidth, billetHeight) : null;
	}

	blockStatuses: any = [];
	// The billet's own picture if one has been uploaded, otherwise the tool's first part.
	get headerImageUrl(): string {
		return this.item?.imageUrl || (this.selectedToolParts || [])[0]?.part?.tileImageUrl || "";
	}

	headerPrimaryButtons: AdminHeaderButton[] = [];
	headerTertiaryButtons: AdminHeaderButton[] = [];
	item: ToolOrder = new ToolOrder();
	itemId = 0;
	itemType = "Billet";
	loading = false;
	machines: Machine[] = [];
	// A billet's material is a MaterialType, not a purchasable material like a part's, so it is named from
	// the enum. getMaterialName looks up purchasable items by id and gave an unrelated part material.
	get materialName(): string {
		return (this.materialTypes || []).find((m: any) => m.id === this.item?.material)?.name || "—";
	}

	materialPurchasableItems: PurchasableItem[] = [];
	materialTypes: any = [];
	newOperation: Operation = new Operation();
	operationWizardStep = 1;
	operationWizardVisible = false;
	operators: UserProfile[] = [];
	planCloseButtonOptions: any = { onClick: () => this.planPopupVisible = false, text: "Close" };
	planItem: any = {};
	planPopupVisible = false;
	planSaveButtonOptions: any = { onClick: () => this.savePlan(), text: "Save Estimate", type: "default" };
	popupAddButtonOptions: any;
	popupCloseButtonOptions: any = { onClick: () => this.closePopup(), text: "Close" };
	popupStage: Operation = new Operation();

	// Stages are stored 1, 2, 3 but the shop floor works in OP10, OP20, OP30.
	get popupStageOpNumber(): number {
		return (this.popupStage?.stageNumber || 0) * 10;
	}

	set popupStageOpNumber(value: number) {
		this.popupStage.stageNumber = Math.max(1, Math.round((value || 10) / 10));
	}

	popupTitle = "";
	popupVisible = false;
	processes: Process[] = [];
	productionTools: ProductionTool[] = [];
	readOnly = true;
	returnUrl = "toolOrders";
	screenMode = "create";
	selectedToolParts: any[] = [];
	// Operations complete / in progress, drawn as a bar on the header card.
	get stageProgress(): any {
		const total = (this.stages || []).length;

		if (total === 0) {
			return null;
		}

		const complete = this.stages.filter((s: any) => s.stageComplete).length;
		const started = this.stages.filter((s: any) => s.stageStarted && !s.stageComplete).length;

		return {
			complete,
			completePercent: (complete / total) * 100,
			started,
			startedPercent: (started / total) * 100,
			total,
		};
	}

	stages: Operation[] = [];
	suggestedName = "";

	get statusName(): string {
		return (this.blockStatuses || []).find((s: any) => s.id === this.item?.status)?.name || "—";
	}
	title: string = "View " + this.itemType;

	get toolName(): string {
		return (this.productionTools || []).find((t: any) => t.id === this.item?.productionToolId)?.name || "—";
	}
	toolroomProcesses: ToolroomProcess[] = [];

	get newOperationOpInUse(): boolean {
		return (this.stages || []).some((s) => s.stageNumber === this.newOperation.stageNumber);
	}

	get newOperationProcessName(): string {
		return this.toolroomProcesses.find((p) => p.id === this.newOperation.toolroomProcessId)?.name || "";
	}

	wizardStages: any[] = [];
	wizardStep = 1;
	wizardTimeUnit = "mins";

	constructor(private router: Router, private toastService: ToastService, private route: ActivatedRoute, private itemService: ToolOrderService, private machineService: MachineService, private productionToolPartService: ProductionToolPartService, private productionToolService: ProductionToolService, private processService: ProcessService, private purchasableItemService: PurchasableItemService, private stageService: OperationService, private userProfileService: UserProfileService, private fileService: FileService, private toolroomProcessService: ToolroomProcessService) {
		this.buildButtons("view");
		this.blockStatuses = GetPropertiesFromEnum(BlockStatusEnum);
		this.materialTypes = GetPropertiesFromEnum(MaterialType);
		this.uploadBlockImage = this.uploadBlockImage.bind(this);
		this.addProgramForOperation = this.addProgramForOperation.bind(this);
		this.operationProcessChanged = this.operationProcessChanged.bind(this);
		this.getMachines();
		this.getMaterialPurchasableItems();
		this.getOperators();
		this.getProcesses();
		this.getProductionTools();
		this.getToolroomProcesses();
		this.isStageCompletable = this.isStageCompletable.bind(this);
		this.isStagePausable = this.isStagePausable.bind(this);
		this.isStageStartable = this.isStageStartable.bind(this);

		this.route.params.subscribe((params) => {
			if (params.id != "create") {
				// Angular reuses this component when the wizard navigates from /create to the new billet,
				// so the constructor's view mode never runs again - reset it here.
				this.cancelEditing();
				if (params.id.split("_").length > 1) {
					this.itemId = params.id.split("_")[0];
					this.getItem(this.itemId);
					this.edit();
				} else {
					this.itemId = params.id;
					this.getItem(params.id);
				}
			} else {
				this.create();
				this.route.queryParams.subscribe((queryParams) => {
					if (queryParams.productionToolId) {
						this.item.productionToolId = +queryParams.productionToolId;
						// Arrived from the tool, so the tool is already chosen: load what step 1 would have
						// loaded on selection and start on the order details instead.
						this.getSelectedToolParts();
						this.wizardStep = 2;
					}
				});
			}
		});

		this.completeStage = this.completeStage.bind(this);
		this.deleteStage = this.deleteStage.bind(this);
		this.editStage = this.editStage.bind(this);
		this.startStage = this.startStage.bind(this);
		this.togglePause = this.togglePause.bind(this);
	}

	// The wizard's last step. The API sets the program name to the new operation's id, and the process
	// description from the chosen process.
	addNewStage() {
		if (!this.newOperation.toolroomProcessId) {
			this.toastService.error("Choose a process");
			this.operationWizardStep = 1;
			return;
		}

		// Inserting between existing operations: the ones from that OP number onwards shift up to make room.
		// Renumbered highest first, so no two operations hold the same OP number along the way.
		const target = this.newOperation.stageNumber || 1;
		const toShift = (this.stages || [])
			.filter((s: any) => (s.stageNumber || 0) >= target)
			.sort((a: any, b: any) => (b.stageNumber || 0) - (a.stageNumber || 0));

		const shifts = toShift.map((stage: any) => () =>
			this.stageService.updateSingleById(stage.id, { operationId: stage.id, stageNumber: (stage.stageNumber || 0) + 1 }));

		const shifted = shifts.length === 0
			? of([])
			: from(shifts).pipe(concatMap((request: any) => request()), toArray());

		shifted.pipe(switchMap(() => this.stageService.createSingle(this.newOperation)))
			.subscribe(
				() => {
					this.operationWizardVisible = false;
					this.getStages();
					this.toastService.success(toShift.length > 0
						? `Operation added, OP${target * 10} onwards shifted up`
						: "Operation added");
				},
				(err: any) => {
					console.log(err);
					ToastApiError(this.toastService, err);
				}
			);
	}

	// Opens the Add Operation wizard, starting the OP number one step past the billet's last operation.
	addNewStagePopup() {
		const lastStage = Math.max(0, ...(this.stages || []).map((s) => s.stageNumber || 0));

		this.newOperation = new Operation({ stageNumber: lastStage + 1, toolOrderId: this.item.id });
		this.operationWizardStep = 1;
		this.operationWizardVisible = true;
	}

	// Create a worksheet/program under a specific operation (programs link to operations). Also passes
	// the tool id (parts/image picker) and the block id (return navigation).
	addProgramForOperation(e: any) {
		this.router.navigate(["admin/productionToolPrograms/create"], { queryParams: { operationId: e.row.data.id, productionToolId: this.item.productionToolId, toolOrderId: this.item.id } });
	}

	buildButtons(mode: string) {
		this.screenMode = mode;
		this.headerPrimaryButtons = [];
		this.headerTertiaryButtons = [];
		if (mode == "edit") {
			this.headerPrimaryButtons = [
				{ method: "updateItem", text: "Save Changes" },
				{ method: "cancelEditing", text: "View Mode" },
				{ method: "close", text: "Close" }
			];
		} else if (mode == "create") {
			this.headerPrimaryButtons = [
				{ method: "createItem", text: "Save" },
				{ method: "close", text: "Close" }
			];
		} else if (mode == "view") {
			this.headerPrimaryButtons = [
				{ method: "edit", text: "Edit" },
				{ method: "close", text: "Close" }
			];
			this.headerTertiaryButtons = [
				{ icon: "fal fa-stopwatch", method: "readyToPlanPopup", text: "Ready To Plan" },
				{ icon: "fal fa-plus", method: "addNewStagePopup", text: "Add Operation" },
				...(!this.item.isComplete ? [{ icon: "fal fa-check", method: "completeOrder", text: "Complete Order" }] : [])
			];
		}
	}

	cancelEditing() {
		this.readOnly = true;
		this.buildButtons("view");
		this.title = "View " + this.itemType;
	}

	// Billets are reached from their tool, so closing one goes back there rather than to the billet list.
	close() {
		if (this.item?.productionToolId != null) {
			this.router.navigate([`/admin/productionTools/${this.item.productionToolId}`]);
			return;
		}

		this.router.navigate(["/admin/toolOrders"]);
	}

	closePopup() {
		this.popupVisible = false;
	}

	completeOrder() {
		if (!confirm("Are you sure you want to mark this block as complete?")) return;
		this.itemService.complete(this.item.id)
			.subscribe(
				() => {
					this.toastService.success("Billet completed");
					this.getItem(this.item.id);
				},
				(err) => {
					console.log(err);
					ToastApiError(this.toastService, err);
				}
			);
	}

	completeStage(e: any) {
		this.stageService.completeStage(e.row.data.id)
			.subscribe(
				() => {
					this.toastService.success("Stage completed");
					this.getStages();
				},
				(err) => {
					console.log(err);
					ToastApiError(this.toastService, err);
				}
			);
	}

	create() {
		this.readOnly = false;
		this.wizardStep = 1;
		this.wizardStages = [];
		this.buildButtons("create");
		this.title = "Create New " + this.itemType;
	}

	createItem() {
		if (!(this.item.name || "").trim()) {
			this.toastService.error("Please name this billet");
			this.wizardStep = 2;
			return;
		}
		this.itemService.createSingle(this.item)
			.subscribe(
				(res: any) => {
					const toolOrderId = res.response?.id || res.id || res.response;
					if (this.wizardStages.length > 0) {
						const stageRequests = this.wizardStages.map((s: any, i: number) => {
							s.toolOrderId = toolOrderId;
							s.stageNumber = i + 1;
							s.estimatedSetupTime = this.wizardTimeUnit === "mins" ? (s.setupInput || 0) / 60 : (s.setupInput || 0);
							s.estimatedRunTime = this.wizardTimeUnit === "mins" ? (s.runInput || 0) / 60 : (s.runInput || 0);
							return this.stageService.createSingle(s);
						});
						forkJoin(stageRequests).subscribe(
							() => {
								this.toastService.success("Billet created with " + this.wizardStages.length + " stages");
								this.router.navigate([`/admin/toolOrders/${toolOrderId}`]);
							},
							(err) => {
								console.log(err);
								ToastApiError(this.toastService, err, "Order created but some stages failed");
								this.router.navigate([`/admin/toolOrders/${toolOrderId}`]);
							}
						);
					} else {
						this.toastService.success("Successfully Created " + this.itemType);
						this.router.navigate([`/admin/toolOrders/${toolOrderId}`]);
					}
				},
				(err) => {
					console.log(err);
					ToastApiError(this.toastService, err);
				}
			);
	}

	deleteStage(e: any) {
		if (confirm("Are you sure you want to delete this stage?")) {
			this.stageService.deleteSingleById(e.row.data.id)
				.subscribe(
					() => {
						this.toastService.success("Stage deleted");
						this.getStages();
					},
					(err) => {
						console.log(err);
						ToastApiError(this.toastService, err);
					}
				);
		}
	}

	edit() {
		this.readOnly = false;
		this.buildButtons("edit");
		this.title = "Edit " + this.itemType;
	}

	editStage(e: any) {
		const stageToEdit = this.stages.find((s) => s.id == e.row.data.id);
		if (!stageToEdit) return;
		this.popupStage = stageToEdit;
		this.popupTitle = "Edit Stage";
		this.popupAddButtonOptions = { onClick: () => this.updateStage(), text: "Save Changes" };
		this.popupVisible = true;
	}

	formatOpNumber(cellInfo: any) {
		return "OP" + (cellInfo.value * 10);
	}

	getItem(itemId: number) {
		this.loading = true;
		this.itemService.getSingleById(itemId)
			.subscribe(
				(res: any) => {
					this.loading = false;
					this.item = res.response;
					this.getStages();
					this.getSelectedToolParts();
					this.buildButtons("view");
				},
				(err) => {
					this.loading = false;
					console.log(err);
					ToastApiError(this.toastService, err);
				}
			);
	}

	getMachineName(machineId?: number | null): string {
		if (!machineId) return "-";
		const m = this.machines.find((x: any) => x.id === machineId);
		return m ? (m as any).cellNumber || "-" : "-";
	}

	getMachines() {
		this.machineService.getBySite(5)
			.subscribe(
				(res: any) => {
					this.machines = res.response;
					this.machines.sort((a: any, b: any) => (a.cellNumber || "").localeCompare(b.cellNumber || ""));
				},
				(err) => console.log(err)
			);
	}

	getMaterialName(materialId?: number | null): string {
		if (!materialId) return "";
		const m = this.materialPurchasableItems.find((x: any) => x.id === materialId);
		return m ? m.name : "";
	}

	getMaterialPurchasableItems() {
		this.purchasableItemService.getAll()
			.subscribe(
				(res: any) => {
					const items = res.response?.items || res.response || [];
					this.materialPurchasableItems = items.filter((pi: any) => pi.purchaseType === PurchaseTypeEnum.Material);
				},
				(err) => console.log(err)
			);
	}

	getOperators() {
		this.userProfileService.bySite(5)
			.subscribe(
				(res: any) => {
					this.operators = res.response;
					this.operators.sort((a: any, b: any) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));
				},
				(err) => console.log(err)
			);
	}

	getProcessName(processId: number): string {
		const p = this.processes.find((x: any) => x.id === processId);
		return p ? p.name : "-";
	}

	getProcesses() {
		this.processService.getAll()
			.subscribe(
				(res: any) => {
					this.processes = res.response.items;
					this.processes.sort((a, b) => a.name.localeCompare(b.name));
				},
				(err) => console.log(err)
			);
	}

	getProductionTools() {
		this.productionToolService.getDataGrid()
			.subscribe(
				(res: any) => {
					this.productionTools = res.response;
					this.productionTools.sort((a: any, b: any) => a.name.localeCompare(b.name));
				},
				(err) => console.log(err)
			);
	}

	// Takes the id from the select box's event: onValueChanged can fire before the two-way binding has
	// written item.productionToolId, so reading the model here missed the parts on the first selection.
	getSelectedToolParts(productionToolId?: number) {
		const toolId = productionToolId || this.item.productionToolId;

		if (!toolId) {
			this.selectedToolParts = [];
			return;
		}

		if (this.screenMode === "create") {
			this.suggestName(toolId);
		}
		this.productionToolPartService.forProductionToolId(toolId)
			.subscribe(
				(res: any) => { this.selectedToolParts = res.response || []; },
				(err) => { console.log(err); this.selectedToolParts = []; }
			);
	}

	getStages() {
		this.stageService.forToolOrder(this.item.id)
			.subscribe(
				(res: any) => {
					this.stages = res.response;
				},
				(err) => console.log(err)
			);
	}

	getToolClient(tool: any): string {
		return tool.client ? tool.client.name : "";
	}

	getToolProject(tool: any): string {
		return tool.project ? tool.project.name : "";
	}

	getToolroomProcesses() {
		this.toolroomProcessService.getAll()
			.subscribe(
				(res: any) => {
					this.toolroomProcesses = (res.response.items as ToolroomProcess[])
						.filter((p) => p.isActive)
						.sort((a, b) => a.name.localeCompare(b.name));
				},
				(err) => console.log(err)
			);
	}

	headerButtonClick(method: any) {
		// @ts-ignore
		if (this[method]) this[method]();
	}

	isStageCompletable(e: any) {
		return e.row.data.stageStarted && !e.row.data.stageComplete;
	}

	isStagePausable(e: any) {
		return e.row.data.stageStarted && !e.row.data.stageComplete;
	}

	isStageStartable(e: any) {
		return !e.row.data.stageStarted && !e.row.data.stageComplete;
	}

	// Open the (optional) machining planner: prefill from whatever billet data the order already has,
	// then let the user enter/adjust the billet size, finished mass and finishing surface area.
	// Picking a process on the edit form keeps the description in step, since the planners read that.
	operationProcessChanged(e: any) {
		const process = this.toolroomProcesses.find((p) => p.id === e.value);

		if (process) {
			this.popupStage.processDescription = process.name;
		}
	}

	operationWizardBack() {
		if (this.operationWizardStep > 1) {
			this.operationWizardStep--;
		}
	}

	operationWizardNext() {
		if (this.operationWizardStep === 1 && !this.newOperation.toolroomProcessId) {
			this.toastService.error("Choose a process");
			return;
		}
		if (this.operationWizardStep < 3) {
			this.operationWizardStep++;
		}
	}

	readyToPlanPopup() {
		this.planItem = {
			billetFinishedMass: this.item.billetFinishedMass,
			billetHeight: this.item.billetHeight,
			billetLength: this.item.billetLength,
			billetSurfaceArea: this.item.billetSurfaceArea,
			billetWidth: this.item.billetWidth,
		};
		this.recalculateBilletEstimate();
		this.planPopupVisible = true;
	}

	// Estimate the machining hours to cut this block down to the finished tool, using the shared billet
	// planner. Only the block dimensions, finished mass and finishing surface area come from the user;
	// everything else (feeds, factors, roughing stages, steel density, contingency) uses the estimator
	// defaults. Operates on the given target — the plan popup's working copy, or the order itself
	// (the optional wizard step writes straight onto the item).
	recalculateBilletEstimate(target: any = this.planItem) {
		const p = target;
		const hasStock = (p.billetLength || 0) > 0 && (p.billetWidth || 0) > 0 && (p.billetHeight || 0) > 0;
		if (hasStock === false || (p.billetFinishedMass || 0) <= 0) {
			p.billetEstimatedRoughingHours = undefined;
			p.billetEstimatedFinishingHours = undefined;
			p.billetEstimatedTotalHours = undefined;
			return;
		}

		const plan = computeBilletPlan({
			...DEFAULT_BILLET_PLAN_INPUTS,
			finishAreaM2: (p.billetSurfaceArea || 0) / 1000000, // user enters mm²
			finishedMassKg: p.billetFinishedMass || 0,
			heightMm: p.billetHeight || 0,
			lengthMm: p.billetLength || 0,
			widthMm: p.billetWidth || 0,
		});
		p.billetEstimatedRoughingHours = plan.roughingTotalHrs;
		p.billetEstimatedFinishingHours = plan.finishingTotalHrs;
		p.billetEstimatedTotalHours = plan.plannedOccupancyHrs;
	}

	// Persist the plan: copy the working copy (inputs + estimate) onto the order and save it.
	savePlan() {
		this.recalculateBilletEstimate();
		const p = this.planItem;
		this.item.billetLength = p.billetLength;
		this.item.billetWidth = p.billetWidth;
		this.item.billetHeight = p.billetHeight;
		this.item.billetFinishedMass = p.billetFinishedMass;
		this.item.billetSurfaceArea = p.billetSurfaceArea;
		this.item.billetEstimatedRoughingHours = p.billetEstimatedRoughingHours;
		this.item.billetEstimatedFinishingHours = p.billetEstimatedFinishingHours;
		this.item.billetEstimatedTotalHours = p.billetEstimatedTotalHours;
		this.itemService.updateSingleById(this.item.id, this.item)
			.subscribe(
				() => {
					this.planPopupVisible = false;
					this.toastService.success("Machining estimate saved.");
				},
				(err) => {
					console.log(err);
					ToastApiError(this.toastService, err, "Could not save the estimate. Please try again.");
				}
			);
	}

	// The wizard works in OP numbers (10, 20, 30) but operations are stored by stage (1, 2, 3).
	setNewOperationOpNumber(value: number) {
		this.newOperation.stageNumber = Math.max(1, Math.round((value || 10) / 10));
	}

	startStage(e: any) {
		this.stageService.startStage(e.row.data.id)
			.subscribe(
				() => {
					this.toastService.success("Stage started");
					this.getStages();
				},
				(err) => {
					console.log(err);
					ToastApiError(this.toastService, err);
				}
			);
	}

	// A billet is named after its tool, numbered past the billets that tool already has:
	// TL-12345-001-B1, -B2 and so on. The user can overwrite it before saving.
	suggestName(productionToolId: number) {
		const tool: any = (this.productionTools || []).find((t: any) => t.id === productionToolId);

		if (!tool) {
			return;
		}

		this.itemService.forProductionTool(productionToolId)
			.subscribe(
				(res: any) => {
					const existing = res.response || [];
					// Only fill in a name the user has not typed over, and not one suggested for another tool.
					if (this.item.name && this.item.name !== this.suggestedName) {
						return;
					}
					this.suggestedName = `${tool.name}-B${existing.length + 1}`;
					this.item.name = this.suggestedName;
				},
				(err) => console.log(err)
			);
	}

	togglePause(e: any) {
		this.stageService.togglePause(e.row.data.id)
			.subscribe(
				() => {
					this.toastService.success("Stage updated");
					this.getStages();
				},
				(err) => {
					console.log(err);
					ToastApiError(this.toastService, err);
				}
			);
	}

	updateItem() {
		this.itemService.updateSingleById(this.item.id, this.item)
			.subscribe(
				() => {
					this.toastService.success("Successfully Updated");
					this.cancelEditing();
				},
				(err) => {
					console.log(err);
					ToastApiError(this.toastService, err);
				}
			);
	}

	updateStage() {
		this.stageService.updateSingleById(this.popupStage.id, this.popupStage)
			.subscribe(
				() => {
					this.popupVisible = false;
					this.getStages();
					this.toastService.success("Stage updated");
				},
				(err) => {
					console.log(err);
					ToastApiError(this.toastService, err);
				}
			);
	}

	// Upload a picture of the block and store its URL on the order.
	uploadBlockImage(file: File) {
		const containerName = "images";
		const uploadData = new FormData();
		uploadData.append("containerName", containerName);
		uploadData.append("files", file);
		uploadData.append("mimeTypeKey", "ImageMimeTypes");
		this.fileService.uploadFile(uploadData)
			.subscribe(
				(res: any) => {
					this.item.imageUrl = `${environment.server.storageUrl}/${containerName}/${res.response[0].location}`;
				},
				(err) => {
					console.log(err);
					ToastApiError(this.toastService, err, "Image upload failed — please upload a valid image file.");
				}
			);
	}

	wizardAddStage() {
		const stage: any = new Operation();
		stage.stageNumber = this.wizardStages.length + 1;
		stage.setupInput = 0;
		stage.runInput = 0;
		this.wizardStages.push(stage);
	}

	wizardBack() {
		if (this.wizardStep > 1) this.wizardStep--;
	}

	wizardNext() {
		if (this.wizardStep === 1 && !this.item.productionToolId) {
			this.toastService.error("Please select a tool");
			return;
		}
		if (this.wizardStep === 2 && !(this.item.name || "").trim()) {
			this.toastService.error("Please name this billet");
			return;
		}
		if (this.wizardStep < 4) this.wizardStep++;
	}

	wizardRemoveStage(index: number) {
		this.wizardStages.splice(index, 1);
		this.wizardStages.forEach((s, i) => s.stageNumber = i + 1);
	}
}
